import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Modal from '../ui/Modal';
import LoadingSpinner from '../ui/Spinner';
import {
  Plus, Edit, Trash2, Download, Search, ArrowUp, ArrowDown,
  RefreshCw, Eye, Save, AlertTriangle,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Database,
  FileText, BarChart3
} from 'lucide-react';
import './EnhancedAdminTable.css';

/**
 * Enhanced Admin Table View - Full CRUD operations for all database tables
 */
const EnhancedAdminTableView = ({ tableName, tableConfig, onBack }) => {
  const [data, setData] = useState([]);
  const [fields, setFields] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableInfo, setTableInfo] = useState({});

  // Filters and sorting
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [formData, setFormData] = useState({});
  const [relatedData, setRelatedData] = useState({});

  // Operation states
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [tableName, currentPage, pageSize, search, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      const queryParams = new URLSearchParams({
        search,
        sort_by: sortBy,
        sort_order: sortOrder,
        page: currentPage.toString(),
        page_size: pageSize.toString(),
      });

      const response = await fetch(`https://vasstrinfo.com/api/admin/table/${tableName}/data/?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch data (${response.status})`);
      }

      const result = await response.json();
      setData(result.data || []);
      setFields(result.fields || []);
      setPagination(result.pagination || {});
      setTableInfo(result.table_info || {});

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedData = async (fieldName) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`https://vasstrinfo.com/api/admin/table/${tableName}/${fieldName}/related/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        setRelatedData(prev => ({
          ...prev,
          [fieldName]: result.data
        }));
      }
    } catch (error) {
      console.error('Error fetching related data:', error);
    }
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');

      const response = await fetch(`https://vasstrinfo.com/api/admin/table/${tableName}/create/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create record');
      }

      setShowCreateModal(false);
      setFormData({});
      fetchData();
      alert('Record created successfully!');

    } catch (error) {
      alert('Error creating record: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');

      const response = await fetch(`https://vasstrinfo.com/api/admin/table/${tableName}/${selectedRecord.id}/update/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update record');
      }

      setShowEditModal(false);
      setSelectedRecord(null);
      setFormData({});
      fetchData();
      alert('Record updated successfully!');

    } catch (error) {
      alert('Error updating record: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record = selectedRecord) => {
    try {
      setDeleting(true);
      const token = localStorage.getItem('access_token');

      const response = await fetch(`https://vasstrinfo.com/api/admin/table/${tableName}/${record.id}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete record');
      }

      setShowDeleteModal(false);
      setSelectedRecord(null);
      fetchData();
      alert('Record deleted successfully!');

    } catch (error) {
      alert('Error deleting record: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      setDeleting(true);
      const token = localStorage.getItem('access_token');

      const response = await fetch(`https://vasstrinfo.com/api/admin/table/${tableName}/bulk-delete/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: selectedRecords }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete records');
      }

      setShowBulkDeleteModal(false);
      setSelectedRecords([]);
      fetchData();
      alert(`${result.deleted_count} records deleted successfully!`);

    } catch (error) {
      alert('Error deleting records: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('access_token');

      const response = await fetch(`https://vasstrinfo.com/api/admin/table/${tableName}/export/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `${tableName}_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      alert('Error exporting data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const openCreateModal = () => {
    setFormData({});
    setShowCreateModal(true);
    // Pre-load related data for foreign key fields
    fields.filter(field => field.type === 'ForeignKey').forEach(field => {
      fetchRelatedData(field.name);
    });
  };

  const openEditModal = (record) => {
    setSelectedRecord(record);
    setFormData({ ...record });
    setShowEditModal(true);
    // Pre-load related data for foreign key fields
    fields.filter(field => field.type === 'ForeignKey').forEach(field => {
      fetchRelatedData(field.name);
    });
  };

  const openDeleteModal = (record) => {
    setSelectedRecord(record);
    setShowDeleteModal(true);
  };

  const openViewModal = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
  };

  const handleSort = (fieldName) => {
    if (sortBy === fieldName) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(fieldName);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleSelectRecord = (recordId) => {
    setSelectedRecords(prev => {
      if (prev.includes(recordId)) {
        return prev.filter(id => id !== recordId);
      } else {
        return [...prev, recordId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedRecords.length === data.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(data.map(record => record.id));
    }
  };

  const renderFieldInput = (field, value, onChange) => {
    const handleChange = (e) => {
      const newValue = field.type === 'BooleanField' ? e.target.checked : e.target.value;
      onChange(field.name, newValue);
    };

    switch (field.type) {
      case 'BooleanField':
        return (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={value || false}
              onChange={handleChange}
            />
            {field.name}
          </label>
        );

      case 'ForeignKey':
        const options = relatedData[field.name] || [];
        return (
          <Select
            value={value || ''}
            onChange={handleChange}
            options={[
              { value: '', label: 'Select...' },
              ...options.map(option => ({
                value: option.id,
                label: option.display
              }))
            ]}
          />
        );

      case 'TextField':
        return (
          <textarea
            value={value || ''}
            onChange={handleChange}
            rows={4}
            className="w-full p-2 border border-gray-300 rounded"
          />
        );

      case 'DateTimeField':
        return (
          <Input
            type="datetime-local"
            value={value ? new Date(value).toISOString().slice(0, 16) : ''}
            onChange={handleChange}
          />
        );

      case 'DateField':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={handleChange}
          />
        );

      case 'IntegerField':
      case 'DecimalField':
        return (
          <Input
            type="number"
            step={field.type === 'DecimalField' ? '0.01' : '1'}
            value={value || ''}
            onChange={handleChange}
          />
        );

      case 'EmailField':
        return (
          <Input
            type="email"
            value={value || ''}
            onChange={handleChange}
          />
        );

      default:
        return (
          <Input
            type="text"
            value={value || ''}
            onChange={handleChange}
            maxLength={field.max_length}
          />
        );
    }
  };

  const formatCellValue = (value, field, record) => {
    // Handle null, undefined, empty values
    if (value === null || value === undefined || value === '') {
      return <span className="field-badge empty">Empty</span>;
    }

    // Handle error values from backend
    if (typeof value === 'string' && value.startsWith('Error:')) {
      return <span className="field-badge error">{value}</span>;
    }

    // Handle non-serializable data
    if (value === 'Non-serializable data' || value === 'RelatedObjects') {
      return <span className="field-badge empty">{value}</span>;
    }

    switch (field.type) {
      case 'BooleanField':
        return (
          <span className={`field-badge ${value ? 'boolean-true' : 'boolean-false'}`}>
            {value ? 'Yes' : 'No'}
          </span>
        );

      case 'DateTimeField':
        try {
          return new Date(value).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (e) {
          return value;
        }

      case 'DateField':
        try {
          return new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        } catch (e) {
          return value;
        }

      case 'EmailField':
        return (
          <a href={`mailto:${value}`} className="text-blue-600 hover:underline text-sm">
            {String(value).length > 30 ? `${String(value).substring(0, 30)}...` : value}
          </a>
        );

      case 'ForeignKey':
        // Check if there's a display field for this foreign key
        const displayValue = record[`${field.name}_display`];
        if (displayValue) {
          return (
            <span className="field-badge foreign-key" title={`ID: ${value}`}>
              {String(displayValue).length > 25 ? `${String(displayValue).substring(0, 25)}...` : displayValue}
            </span>
          );
        }
        return <span className="field-badge foreign-key">ID: {value}</span>;

      case 'DecimalField':
      case 'FloatField':
        return (
          <span className="text-sm font-mono text-gray-700">
            {typeof value === 'number' ? value.toFixed(2) : String(value)}
          </span>
        );

      case 'IntegerField':
      case 'BigIntegerField':
      case 'SmallIntegerField':
      case 'PositiveIntegerField':
        return (
          <span className="text-sm font-mono text-gray-700">
            {typeof value === 'number' ? value.toLocaleString() : String(value)}
          </span>
        );

      case 'ImageField':
      case 'FileField':
        if (value && value.startsWith('http')) {
          const fileName = value.split('/').pop();
          return (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
              title="Click to view file"
            >
              {fileName}
            </a>
          );
        }
        return value || <span className="text-gray-400">No file</span>;

      case 'URLField':
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
            title="Open link"
          >
            {value.length > 30 ? `${value.substring(0, 30)}...` : value}
          </a>
        );

      case 'TextField':
        const textValue = String(value);
        if (textValue.length > 100) {
          return (
            <span title={textValue} className="cursor-help text-sm">
              {textValue.substring(0, 100)}...
            </span>
          );
        }
        return <span className="text-sm whitespace-pre-wrap">{textValue}</span>;

      case 'CharField':
        const charValue = String(value);
        if (charValue.length > 40) {
          return (
            <span title={charValue} className="cursor-help text-sm">
              {charValue.substring(0, 40)}...
            </span>
          );
        }
        return <span className="text-sm">{charValue}</span>;

      case 'JSONField':
        try {
          const jsonObj = typeof value === 'string' ? JSON.parse(value) : value;
          const jsonStr = JSON.stringify(jsonObj, null, 2);
          return (
            <span
              title={jsonStr}
              className="field-badge json-field"
            >
              JSON ({Object.keys(jsonObj || {}).length} keys)
            </span>
          );
        } catch (e) {
          return <span className="field-badge error">Invalid JSON</span>;
        }

      default:
        const stringValue = String(value);
        if (stringValue.length > 50) {
          return (
            <span title={stringValue} className="cursor-help text-sm">
              {stringValue.substring(0, 50)}...
            </span>
          );
        }
        return <span className="text-sm">{stringValue}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <AlertTriangle className="w-5 h-5 text-red-400 mr-3 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <Button
              onClick={fetchData}
              variant="secondary"
              size="sm"
              className="mt-2"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="enhanced-admin-table">
      {/* Header */}
      <div className="table-header">
        <div className="table-header-left">
          <button onClick={onBack} className="btn btn-secondary btn-sm">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <div className="table-title">
            <h2>{tableConfig?.label || tableName}</h2>
            <p className="table-description">{tableConfig?.description}</p>
          </div>
        </div>

        <div className="table-header-right">
          {selectedRecords.length > 0 && (
            <button
              onClick={() => setShowBulkDeleteModal(true)}
              className="btn btn-danger btn-sm"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected ({selectedRecords.length})
            </button>
          )}

          <button
            onClick={handleExport}
            className="btn btn-secondary btn-sm"
            disabled={exporting}
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>

          {!tableInfo.readonly && (
            <button
              onClick={openCreateModal}
              className="btn btn-primary btn-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New
            </button>
          )}

          <button
            onClick={fetchData}
            className="btn btn-secondary btn-sm"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Controls Section */}
      <div className="table-controls">
        <div className="controls-left">
          <div className="search-container">
            <Search className="search-icon" size={16} />
            <input
              type="text"
              placeholder={`Search ${tableConfig?.label || tableName}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>

          <select
            value={pageSize.toString()}
            onChange={(e) => setPageSize(parseInt(e.target.value))}
            className="btn btn-secondary btn-sm"
          >
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
        </div>

        <div className="controls-right">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="btn btn-secondary btn-sm"
          >
            <option value="id">Sort by ID</option>
            {fields.map(field => (
              <option key={field.name} value={field.name}>
                Sort by {field.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="btn btn-secondary btn-sm"
          >
            {sortOrder === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
            {sortOrder.toUpperCase()}
          </button>
        </div>
      </div>

      {/* Table Stats */}
      {pagination && (
        <div className="table-stats">
          <div className="stats-item">
            <Database className="stats-item-icon" />
            <div>
              <div className="stats-item-label">Total Records</div>
              <div className="stats-item-value">{pagination.total_items}</div>
            </div>
          </div>
          <div className="stats-item">
            <FileText className="stats-item-icon" />
            <div>
              <div className="stats-item-label">Showing</div>
              <div className="stats-item-value">
                {Math.min((pagination.current_page - 1) * pageSize + 1, pagination.total_items)} - {Math.min(pagination.current_page * pageSize, pagination.total_items)}
              </div>
            </div>
          </div>
          <div className="stats-item">
            <BarChart3 className="stats-item-icon" />
            <div>
              <div className="stats-item-label">Fields</div>
              <div className="stats-item-value">{fields.length}</div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th className="select-column">
                  <input
                    type="checkbox"
                    checked={selectedRecords.length === data.length && data.length > 0}
                    onChange={handleSelectAll}
                    className="select-checkbox"
                  />
                </th>
                {fields.filter(field => field.name !== 'id' || field.name === 'id').map((field) => (
                  <th
                    key={field.name}
                    className={`sortable ${sortBy === field.name ? 'sorted' : ''}`}
                    onClick={() => handleSort(field.name)}
                  >
                    <div className="th-content">
                      <span>{field.name}</span>
                      {sortBy === field.name && (
                        sortOrder === 'asc' ?
                        <ArrowUp className="w-3 h-3" /> :
                        <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                ))}
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((record) => (
                <tr key={record.id} className={selectedRecords.includes(record.id) ? 'selected' : ''}>
                  <td className="select-column">
                    <input
                      type="checkbox"
                      checked={selectedRecords.includes(record.id)}
                      onChange={() => handleSelectRecord(record.id)}
                      className="select-checkbox"
                    />
                  </td>
                  {fields.filter(field => field.name !== 'id' || field.name === 'id').map((field) => (
                    <td key={field.name}>
                      <div className="cell-content">
                        {formatCellValue(record[field.name], field, record)}
                      </div>
                    </td>
                  ))}
                  <td className="actions-column">
                    <div className="action-buttons">
                      <button
                        onClick={() => openViewModal(record)}
                        className="action-button view"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {!tableInfo.readonly && (
                        <>
                          <button
                            onClick={() => openEditModal(record)}
                            className="action-button edit"
                            title="Edit Record"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(record)}
                            className="action-button delete"
                            title="Delete Record"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {((pagination.current_page - 1) * pageSize) + 1} to {Math.min(pagination.current_page * pageSize, pagination.total_items)} of {pagination.total_items} results
          </div>

          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={!pagination.has_previous}
              className="btn btn-secondary btn-sm"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>

            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!pagination.has_previous}
              className="btn btn-secondary btn-sm"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <input
              type="number"
              min="1"
              max={pagination.total_pages}
              value={currentPage}
              onChange={(e) => {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= pagination.total_pages) {
                  setCurrentPage(page);
                }
              }}
              className="page-input"
            />

            <span className="pagination-info">
              of {pagination.total_pages}
            </span>

            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!pagination.has_next}
              className="btn btn-secondary btn-sm"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => setCurrentPage(pagination.total_pages)}
              disabled={!pagination.has_next}
              className="btn btn-secondary btn-sm"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={`Create New ${tableConfig?.label || tableName}`}
        size="large"
      >
        <div className="form-grid">
          {fields.filter(field => !['id'].includes(field.name)).map((field) => (
            <div key={field.name} className="form-field">
              <label>{field.name} {field.required && <span className="required">*</span>}</label>
              {renderFieldInput(field, formData[field.name], (name, value) =>
                setFormData(prev => ({ ...prev, [name]: value }))
              )}
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <Button onClick={() => setShowCreateModal(false)} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleCreate} loading={saving} variant="primary">
            <Save className="w-4 h-4 mr-2" />
            Create
          </Button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={`Edit ${tableConfig?.label || tableName}`}
        size="large"
      >
        <div className="form-grid">
          {fields.filter(field => field.name !== 'id').map((field) => (
            <div key={field.name} className="form-field">
              <label>{field.name} {field.required && <span className="required">*</span>}</label>
              {renderFieldInput(field, formData[field.name], (name, value) =>
                setFormData(prev => ({ ...prev, [name]: value }))
              )}
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <Button onClick={() => setShowEditModal(false)} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleUpdate} loading={saving} variant="primary">
            <Save className="w-4 h-4 mr-2" />
            Update
          </Button>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={`View ${tableConfig?.label || tableName} Details`}
        size="large"
      >
        {selectedRecord && (
          <div className="form-grid">
            {fields.map((field) => (
              <div key={field.name} className="form-field">
                <label className="font-semibold text-gray-700">
                  {field.name}
                  <span className="text-xs text-gray-500 ml-2">({field.type})</span>
                </label>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg min-h-[40px]">
                  {formatCellValue(selectedRecord[field.name], field, selectedRecord)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="modal-actions">
          <button
            onClick={() => setShowViewModal(false)}
            className="btn btn-secondary"
          >
            Close
          </button>
          {!tableInfo.readonly && (
            <button
              onClick={() => {
                setShowViewModal(false);
                openEditModal(selectedRecord);
              }}
              className="btn btn-primary"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
          )}
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Delete"
      >
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p>Are you sure you want to delete this record?</p>
          <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
        </div>

        <div className="modal-actions">
          <Button onClick={() => setShowDeleteModal(false)} variant="secondary">
            Cancel
          </Button>
          <Button onClick={() => handleDelete()} loading={deleting} variant="danger">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </Modal>

      {/* Bulk Delete Modal */}
      <Modal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        title="Confirm Bulk Delete"
      >
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p>Are you sure you want to delete {selectedRecords.length} records?</p>
          <p className="text-sm text-gray-500 mt-2">This action cannot be undone.</p>
        </div>

        <div className="modal-actions">
          <Button onClick={() => setShowBulkDeleteModal(false)} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleBulkDelete} loading={deleting} variant="danger">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete {selectedRecords.length} Records
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default EnhancedAdminTableView;